import { Authenticators, isGoogleOneClickMustChooseUserAccountError } from '@customerio/hydra-authentication';
import { sessionsStore } from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { isEmptyString } from '@/utils/string';
import {
  fetchOidcAuthorizationEndpoint,
  openAzureOIDCPopup,
  openGenericOIDCPopup,
  openGoogleOIDCPopup,
  openOktaOIDCPopup,
} from './providers';
import { openSAMLPopup } from './saml-popup';
import { mergeSamlSessionWithLocal } from './saml-session';
import type { SsoConfiguration } from './sso-info';

/**
 * Metadata every login authenticator sends alongside the credentials.
 * `add_session: false` replaces the active session slot (a plain login or an
 * in-place re-auth); `true` adds a second login next to it.
 */
export type LoginMeta = {
  ga_client_id: string;
  add_session: boolean;
};

export type SsoAuthenticateOptions = {
  userExternalIdentityEmail: string;
  meta: LoginMeta;
};

type OidcPopupOpener = typeof openOktaOIDCPopup;

/** Strip a `+alias` from the local part of an email (Google ignores it). */
export function removeGoogleEmailAlias(email: string): string {
  return email.replace(/\+[^@]*(?=@)/, '');
}

/**
 * Google "one-click" sign-in — the flow for users whose identity provider is
 * Google but who are NOT behind account-required SSO (so `login_sso_info`
 * returns nothing). Opens the Google popup and runs the `GoogleOneClick`
 * authenticator. `userId` scopes the exchange to the known user (as the Ember
 * reauth modal does), avoiding the multi-account chooser on re-auth.
 */
export async function authenticateGoogleOneClick(email: string, userId: number, meta: LoginMeta): Promise<unknown> {
  const { authorizationCode, redirectUri } = await openGoogleOIDCPopup();
  try {
    return await session.authenticate(Authenticators.GoogleOneClick, {
      code: authorizationCode,
      redirect_uri: redirectUri,
      email,
      user_id: String(userId),
      ...meta,
    });
  } catch (error) {
    // A Google login that maps to more than one account: finish for the known
    // user (their id is fixed during re-auth), skipping the account chooser.
    // Other errors — including 2FA-required — propagate to the caller.
    if (isGoogleOneClickMustChooseUserAccountError(error)) {
      return session.authenticate(Authenticators.GoogleOneClickChooseUserFinish, {
        user_id: String(userId),
        choose_user_jwt_token: error.chooseUserJWTToken,
        ...meta,
      });
    }
    throw error;
  }
}

async function authenticateSamlSso(config: SsoConfiguration, email: string): Promise<unknown> {
  const payload = await openSAMLPopup({ authorizationURL: config.saml_issuer_url ?? '', email });
  const data = payload == null ? undefined : mergeSamlSessionWithLocal(payload, sessionsStore.get());
  return session.authenticate(Authenticators.SAML, data);
}

async function authenticateGoogleSso(
  config: SsoConfiguration,
  email: string,
  { userExternalIdentityEmail, meta }: SsoAuthenticateOptions,
): Promise<unknown> {
  const loginHint = !isEmptyString(userExternalIdentityEmail)
    ? userExternalIdentityEmail
    : removeGoogleEmailAlias(email);
  const { authorizationCode, redirectUri } = await openGoogleOIDCPopup({
    hostedDomain: config.oidc_google_hosted_domain ?? undefined,
    loginHint,
  });
  return session.authenticate(Authenticators.SSO, {
    email,
    code: authorizationCode,
    redirect_uri: redirectUri,
    ...meta,
  });
}

// OIDC providers advertise their authorization endpoint via .well-known.
async function authenticateOidcSso(
  config: SsoConfiguration,
  email: string,
  meta: LoginMeta,
  openPopup: OidcPopupOpener,
): Promise<unknown> {
  const authorizationURL = await fetchOidcAuthorizationEndpoint(config.oidc_issuer_url ?? '');
  const { authorizationCode, redirectUri } = await openPopup({
    authorizationURL,
    clientID: config.oidc_client_id ?? '',
  });
  return session.authenticate(Authenticators.SSO, {
    email,
    code: authorizationCode,
    redirect_uri: redirectUri,
    ...meta,
  });
}

/**
 * Open the right IdP popup for the SSO config and authenticate. Returns the raw
 * authenticate response (callers post-process it — e.g. the login flow's
 * multi-account handling).
 */
export function authenticateWithSso(
  config: SsoConfiguration,
  email: string,
  opts: SsoAuthenticateOptions,
): Promise<unknown> {
  switch (config.identity_provider) {
    case 'generic_saml':
      return authenticateSamlSso(config, email);
    case 'google_oidc':
      return authenticateGoogleSso(config, email, opts);
    case 'okta_oidc':
      return authenticateOidcSso(config, email, opts.meta, openOktaOIDCPopup);
    case 'azure_oidc':
      return authenticateOidcSso(config, email, opts.meta, openAzureOIDCPopup);
    case 'generic_oidc':
      return authenticateOidcSso(config, email, opts.meta, openGenericOIDCPopup);
    default: {
      const _exhaustive: never = config.identity_provider;
      throw new Error(`Unsupported SSO provider: ${String(_exhaustive)}`);
    }
  }
}
